import Cookies from 'js-cookie'
import {getPostSlug, lpTypeOf} from './utils'
import config from './config.json'


// どの経路から流入してきたのかという情報をLPに引き継ぐため、流入経路の情報を一定期間保持する
class OverrideParamsStore {
  overridableParams() {
    return ['utm_source', 'utm_campaign', 'utm_content', 'utm_medium', 'utm_term']
  }

  expiredAt() {
    // ユーザーの97.7%はセッション継続時間が1時間以内であるため、1時間あれば十分。
    return new Date(new Date().getTime() + 60 * 60 * 1000)
  }

  setParams(params) {
    Object.keys(this.permittedParams(params)).forEach((key) => {
      Cookies.set(key, this.paramValue(params, key), {
        expires: this.expiredAt(),
      })
    })
  }

  getParams() {
    const overridableParams = this.overridableParams()
    return overridableParams.reduce((ret, param) => {
      const stored = Cookies.get(param)
      if (stored === undefined) return ret
      return {...ret, [param]: stored}
    }, {})
  }

  permittedParams(params) {
    const overridableParams = this.overridableParams()
    return this.paramEntries(params)
      .filter((param) => overridableParams.includes(param))
      .reduce((ret, param) => {
        return {...ret, [param]: this.paramValue(params, param)}
      }, {})
  }

  paramEntries(params) {
    if (params instanceof URLSearchParams) {
      return Array.from(params.keys())
    }

    return Object.keys(params)
  }

  paramValue(params, param) {
    if (params instanceof URLSearchParams) {
      return params.get(param)
    }

    return params[param]
  }
}


// 実験ごとのマーカー Cookie（hapisumu_ab_mk_<test_id>=<variant>）を全部読み、
// test_id 昇順で `test_id(variant)` を '/' 連結したマーカー文字列を組み立てる。
const AB_MARKER_PREFIX = 'hapisumu_ab_mk_'

function buildAbCampaignMarker() {
  // session応答前にも終了テストのマーカーをLPへ渡さない。各終了反映90日後に削除可能。
  for (const testId of ['cta_form_heading', 'article_company_cta']) {
    const name = AB_MARKER_PREFIX + testId
    if (Cookies.get(name) !== undefined) Cookies.remove(name, {path: '/'})
  }
  const all = Cookies.get() || {}
  return Object.keys(all)
    .filter((name) => name.startsWith(AB_MARKER_PREFIX))
    .map((name) => ({ testId: name.slice(AB_MARKER_PREFIX.length), variant: all[name] }))
    .filter((entry) => entry.testId !== '' && entry.variant)
    .sort((a, b) => (a.testId < b.testId ? -1 : a.testId > b.testId ? 1 : 0))
    .map((entry) => `${entry.testId}(${entry.variant})`)
    .join('/')
}

// 適用すべきABマーカーを返す。流入引き継ぎ（overrideParams）に utm_campaign があれば、
// その流入アトリビューション（press では DSA）を優先してマーカーは適用しない。
// 引き継ぎが無いときだけ、保持中の割当マーカー Cookie から組み立てて返す。
function abCampaignMarker(overrideParams) {
  if (overrideParams.utm_campaign) return ''
  return buildAbCampaignMarker()
}

// ABテストの割当マーカー（hapisumu_ab_mk_* から組み立てた文字列）を utm_campaign に適用する。
// utm_content は触らない: 掲載面は utm_medium に入っており、utm_content には next_step_link 等の
// 「ページ内のどのリンクか」を表す固有値が入ることがあるため、上書きすると属性が失われる。
// マーカーが無い（非対象/DSA）場合は何もしない。
function applyAbCampaignMarker(setCampaign, abCampaign) {
  if (!abCampaign) return
  setCampaign('utm_campaign', abCampaign)
}

export class LpRewriter {
  constructor(postSlug, overrideParams, abCampaign = '', pagePath = '') {
    this.postSlug = postSlug
    this.overrideParams = overrideParams
    this.abCampaign = abCampaign
    this.pagePath = pagePath
  }

  rewrite(url) {
    const lpType = lpTypeOf(url)
    if (!lpType) return url

    const uri = this.toUri(url)
    uri.pathname = this.resolveLpPath(uri.pathname, lpType)

    // どの記事からの流入か把握するためにutm_termの値は基本的に記事のslugを設定する
    uri.searchParams.set("utm_term", this.postSlug)

    Object.keys(this.overrideParams).forEach((key) => {
      uri.searchParams.set(key, this.overrideParams[key])
    })

    applyAbCampaignMarker(
      (name, value) => uri.searchParams.set(name, value),
      this.abCampaign,
    )

    const notContainDomain = url.startsWith('/')
    if (notContainDomain) return uri.pathname + '?' + uri.searchParams.toString()
    return uri.toString()
  }

  rewritePath(url) {
    const uri = this.toUri(url)

    uri.pathname = this.resolveLpPath(uri.pathname, lpTypeOf(url))
    return uri.toString()
  }

  // 割り当てルール（rules: page_pattern が今いるページのパスにマッチしたら専用LPへ、先勝ち）を適用し、
  // どれにも当たらなければデフォルト（path）のLPへ貼り直す
  resolveLpPath(path, lpType) {
    const lpConfig = config.lp[lpType]
    if (!lpConfig || config.replacement_exclusion_list.includes(path)) return path

    const rules = lpConfig.rules || []
    const matched = rules.find((rule) => new RegExp(rule.page_pattern).test(this.pagePath))
    return matched ? matched.path : lpConfig.path
  }

  toUri(url) {
    if (!url || typeof url !== 'string') {
      throw new Error('Invalid URL: URL must be a non-empty string')
    }
    let dummyBase = url.startsWith('/') ? 'http://hapisumu.jp/' : undefined // パスのみの時にはURLはインスタンス化できないので仮のドメインを設定する
    return new URL(url, dummyBase)
  }
}

export function rewriteLpLinks() {
  const links = document.getElementsByTagName('a')
  if (links.length === 0) return

  const queryParams = new URL(window.location.href).searchParams
  const store = new OverrideParamsStore()
  store.setParams(queryParams)
  const overrideParams = store.getParams()
  const abCampaign = abCampaignMarker(overrideParams)

  const rewriter = new LpRewriter(getPostSlug(), overrideParams, abCampaign, window.location.pathname)

  Array.from(links).forEach(function (a) {
    const href = rewriter.rewrite(a.href)
    if (a.getAttribute('href') !== href) a.href = href
  })
}

export function rewriteFormUtmInput() {
  const forms = document.querySelectorAll('.reform-lp-form');
  if (!forms.length) return;

  const queryParams = new URL(window.location.href).searchParams;
  const store = new OverrideParamsStore();
  store.setParams(queryParams);
  const overrideParams = store.getParams();
  const abCampaign = abCampaignMarker(overrideParams);

  const rewriter = new LpRewriter(getPostSlug(), overrideParams, '', window.location.pathname);

  function setOrCreateHiddenInput(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    if (input.value !== value) input.value = value;
  }

  forms.forEach(form => {
    if (form.action && typeof form.action === 'string') {
      try {
        const action = rewriter.rewritePath(form.action);
        if (form.getAttribute('action') !== action) form.action = action;
      } catch (e) {
        console.error('Failed to rewrite form action:', e);
      }
    }

    if (overrideParams.utm_source) {
      setOrCreateHiddenInput(form, 'utm_source', overrideParams.utm_source || 'press');
    }
    if (overrideParams.utm_term) {
      setOrCreateHiddenInput(form, 'utm_term', overrideParams.utm_term);
    }
    if (overrideParams.utm_medium) {
      setOrCreateHiddenInput(form, 'utm_medium', overrideParams.utm_medium);
    }
    if (overrideParams.utm_campaign) {
      setOrCreateHiddenInput(form, 'utm_campaign', overrideParams.utm_campaign);
    }

    Object.keys(overrideParams)
        .filter(param => !['utm_source', 'utm_term', 'utm_medium', 'utm_campaign'].includes(param))
        .forEach(param => {
          setOrCreateHiddenInput(form, param, overrideParams[param]);
        });

    applyAbCampaignMarker(
      (name, value) => setOrCreateHiddenInput(form, name, value),
      abCampaign,
    );
  });
}
